'use client';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Language } from '@/types/language';

interface LanguageSelectorProps {
    languages: Language[];
}

export function LanguageSelector({ languages }: LanguageSelectorProps) {
    const locale = useLocale();
    const pathname = usePathname();

    // Get current language object
    const currentLanguage = languages.find(lang => lang.locale === locale);

    // Create a path for the new locale
    const createLocalePath = (newLocale: string) => {
        const pathParts = pathname.split('/');
        // Remove empty string and current locale
        const pathWithoutLocale = pathParts.slice(2).join('/');
        // Return new path with the new locale
        return pathWithoutLocale ? `/${newLocale}/${pathWithoutLocale}` : `/${newLocale}`;
    };

    const handleLanguageChange = (locale: string) => {
        // Check if the selected language exists in the list of available languages
        const selectedLanguage = languages.find(lang => lang.locale === locale);

        // If the language is found and is active, redirect to the page with this language
        if (selectedLanguage && selectedLanguage.active) {
            window.location.href = createLocalePath(locale);
        } else {
            console.error('Selected language not available or not active:', locale);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant='ghost'
                    className='h-auto rounded-none p-0 hover:bg-transparent focus-visible:ring-0'
                    aria-label='Change language'
                >
                    {currentLanguage && currentLanguage.image ? (
                        <Image
                            src={currentLanguage.image}
                            alt={currentLanguage.title}
                            width={40}
                            height={40}
                            className='cursor-pointer rounded-full'
                        />
                    ) : (
                        <span className='flex size-6 items-center justify-center rounded-full bg-gray-200'>
                            {currentLanguage ? currentLanguage.title.charAt(0) : locale.charAt(0).toUpperCase()}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='min-w-[50px] focus-visible:ring-0'>
                {languages
                    .filter(lang => lang.active)
                    .map(language => (
                        <DropdownMenuItem
                            key={language.id}
                            className={language.locale === locale ? 'p-1 font-bold' : 'p-1'}
                            onClick={() => handleLanguageChange(language.locale)}
                        >
                            {language.image ? (
                                <Image
                                    src={language.image}
                                    alt={language.title}
                                    width={40}
                                    height={40}
                                    className='cursor-pointer rounded-full'
                                />
                            ) : (
                                <span className='flex size-10 items-center justify-center rounded-full bg-gray-200'>
                                    {language.title.charAt(0)}
                                </span>
                            )}
                        </DropdownMenuItem>
                    ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
